import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getStaticPagePath, STATIC_PAGE_METADATA, staticPageCanonical } from "./staticPageMetadata";

const DEFAULT_TITLE = "Plutus Expert Investing";
const DEFAULT_DESCRIPTION = STATIC_PAGE_METADATA["/"].description;

/** Keep prerendered metadata correct when React navigates without a document reload. */
export default function StaticPageHead(): null {
  const { pathname } = useLocation();
  const pagePath = getStaticPagePath(pathname);

  useEffect(() => {
    if (!pagePath) return;
    const metadata = STATIC_PAGE_METADATA[pagePath];
    const image = "image" in metadata ? metadata.image : null;
    const canonicalUrl = staticPageCanonical(pagePath);
    document.title = metadata.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) description.content = metadata.description;
    document.head.querySelectorAll("[data-static-page]").forEach(metadataElement => metadataElement.remove());

    const canonicalLink = document.createElement("link");
    canonicalLink.rel = "canonical";
    canonicalLink.href = canonicalUrl;
    canonicalLink.setAttribute("data-static-page", "");
    document.head.appendChild(canonicalLink);
    const openGraphContentByProperty: Record<string, string> = {
      "og:title": metadata.title,
      "og:description": metadata.description,
      "og:url": canonicalUrl,
      "og:type": "website",
      ...(image ? {
        "og:image": image.url,
        "og:image:type": image.type,
        "og:image:width": String(image.width),
        "og:image:height": String(image.height),
        "og:image:alt": image.alt,
      } : {}),
    };
    const metadataElements: HTMLElement[] = [canonicalLink];
    for (const [propertyName, propertyContent] of Object.entries(openGraphContentByProperty)) {
      const metadataElement = document.createElement("meta");
      metadataElement.setAttribute("property", propertyName);
      metadataElement.content = propertyContent;
      metadataElement.setAttribute("data-static-page", "");
      document.head.appendChild(metadataElement);
      metadataElements.push(metadataElement);
    }
    const twitterContentByName: Record<string, string> = {
      "twitter:title": metadata.title,
      "twitter:description": metadata.description,
      ...(image ? {
        "twitter:card": "summary_large_image",
        "twitter:image": image.url,
        "twitter:image:alt": image.alt,
      } : {}),
    };
    for (const [name, content] of Object.entries(twitterContentByName)) {
      const metadataElement = document.createElement("meta");
      metadataElement.name = name;
      metadataElement.content = content;
      metadataElement.setAttribute("data-static-page", "");
      document.head.appendChild(metadataElement);
      metadataElements.push(metadataElement);
    }

    return () => {
      metadataElements.forEach(metadataElement => metadataElement.remove());
      // Do not overwrite a destination page that has already set its own metadata.
      if (document.title === metadata.title) document.title = DEFAULT_TITLE;
      if (description?.content === metadata.description) description.content = DEFAULT_DESCRIPTION;
    };
  }, [pagePath]);

  return null;
}
